import { useCallback, useEffect, useMemo, useState } from "react";
import { supabaseUrl, supabaseAnonKey } from "../supabase/config";
import { getQrCustomerSessionToken } from "../supabase/qrCustomerSessionService";
import { collectMenuTranslationTexts } from "../supabase/edge-functions/_shared/menuTranslationCatalog";
import { translateQrUi } from "./qrTranslations";

const PREFIX = "mozzoapp:menu-translations:v1:";
const EMPTY = Object.freeze({});
const read = (key) => {
  try {
    const value = JSON.parse(localStorage.getItem(PREFIX + key) || "null");
    return value && typeof value === "object" && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).filter(([, text]) => typeof text === "string")) : {};
  } catch { return {}; }
};

export function useQrMenuTranslation({ language, qrToken, payload, promotions, enabled = true }) {
  const scope = `${payload?.branch?.id || ""}:${language}`;
  const texts = useMemo(() => collectMenuTranslationTexts(payload, promotions.filter((item) => item.active !== false)), [payload, promotions]);
  const signature = JSON.stringify(texts);
  const [state, setState] = useState({ scope: "", translations: {}, status: "idle" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled || language === "es" || !qrToken || !payload?.branch?.id || !supabaseUrl) return;
    const controller = new AbortController();
    let stopped = false;
    let timer;
    const translations = read(scope);
    const missing = () => texts.some((value) => typeof translations[value] !== "string");
    const publish = (status) => { if (!stopped) setState({ scope, translations: { ...translations }, status }); };
    publish(missing() ? "loading" : "ready");
    if (!missing()) return () => { stopped = true; };
    const wait = (ms) => new Promise((resolve) => { timer = setTimeout(resolve, ms); });
    const run = async () => {
      try {
        for (let batch = 0; batch < 60 && !stopped; batch += 1) {
          const timeout = setTimeout(() => controller.abort(), 40000);
          let response;
          try {
            response = await fetch(`${supabaseUrl}/functions/v1/public-menu-translations`, {
              method: "POST", signal: controller.signal,
              headers: { "Content-Type": "application/json", apikey: supabaseAnonKey, Authorization: `Bearer ${supabaseAnonKey}` },
              body: JSON.stringify({ language, qrToken, customerSessionToken: getQrCustomerSessionToken(qrToken) }),
            });
          } finally { clearTimeout(timeout); }
          if (!response.ok) throw new Error("Translation unavailable");
          const result = await response.json();
          // Match exact source strings: edits are never given a stale translation.
          for (const value of texts) {
            if (typeof result.translations?.[value] === "string") translations[value] = result.translations[value];
          }
          if (stopped) return;
          try { localStorage.setItem(PREFIX + scope, JSON.stringify(translations)); } catch { /* Private browsing/quota. */ }
          publish(result.complete ? "ready" : "loading");
          if (result.complete) return;
          await wait(Math.min(5000, Math.max(250, Number(result.retryAfterMs) || 2000)));
        }
        publish("error");
      } catch { publish("error"); }
    };
    run();
    return () => { stopped = true; clearTimeout(timer); controller.abort(); };
    // signature, rather than object identity, avoids refetching on stock refreshes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, language, qrToken, scope, signature, attempt, payload?.branch?.id]);
  const translations = state.scope === scope ? state.translations : EMPTY;
  const tx = useCallback((text, variables) => translateQrUi(language, text, variables, translations), [language, translations]);
  return { tx, status: language === "es" || !enabled ? "idle" : state.scope === scope ? state.status : "loading", retry: () => setAttempt((value) => value + 1) };
}
